import { CategoryEntity, GetIssuesByAssetRequestDTO, GetIssuesRequestDTO, IssueStatus, RecordState, TagEntity } from '@gearbelt/global';
import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';

import { ApiService } from '@/services/api-v2';
import { AWAApiV2Service } from '@/services/awa-api-v2';

export const issueKeys = {
  all: ['issues'] as const,
  detail: (issueId: string | undefined, isAWA: boolean) => [...issueKeys.all, 'detail', issueId, isAWA] as const,
  byAsset: (assetId: string, isAWA: boolean, filters?: GetIssuesByAssetRequestDTO['filter']) =>
    [...issueKeys.all, 'by-asset', assetId, isAWA, filters] as const,
  byRepair: (repairNumber: number | undefined, isAWA: boolean) => [...issueKeys.all, 'by-repair', repairNumber, isAWA] as const,
  awaByAsset: (assetId: string, status?: IssueStatus[], orderBy?: Parameters<typeof AWAApiV2Service.GetIssues>[0]['orderBy']) =>
    [...issueKeys.all, 'awa-by-asset', assetId, status, orderBy] as const,
  actions: (issueId: string | undefined, isAWA: boolean) => [...issueKeys.all, 'actions', issueId, isAWA] as const,
  infinite: (filters?: GetIssuesRequestDTO['filters'], groupBy?: GetIssuesRequestDTO['groupBy'], orderBy?: GetIssuesRequestDTO['orderBy']) =>
    [...issueKeys.all, 'infinite', filters, groupBy, orderBy] as const,
};

export const issueCategoryKeys = {
  all: ['issue-categories'] as const,
  list: (orgId: string | undefined) => [...issueCategoryKeys.all, orgId] as const,
};

export const issueTagKeys = {
  all: ['issue-tags'] as const,
  list: (orgId: string | undefined) => [...issueTagKeys.all, orgId] as const,
};

export const getAssetIssuesQueryOptions = ({
  assetId,
  isAWA = false,
  filters,
}: {
  assetId: string;
  isAWA?: boolean;
  filters?: GetIssuesByAssetRequestDTO['filter'];
}) => {
  return queryOptions({
    queryKey: issueKeys.byAsset(assetId, isAWA, filters),
    queryFn: async () => (await (await (isAWA ? AWAApiV2Service : ApiService).GetIssuesByAsset({ assetId, filter: filters })).json()).issues,
    enabled: !!assetId,
  });
};

export const getRepairIssuesQueryOptions = ({ repairNumber, isAWA = false }: { repairNumber: number | undefined; isAWA?: boolean }) => {
  return queryOptions({
    queryKey: issueKeys.byRepair(repairNumber, isAWA),
    queryFn: async () => {
      if (!repairNumber) throw new Error('Repair number is required');
      return (
        await (isAWA ? AWAApiV2Service : ApiService).GetIssues({
          filters: { fields: { repairNumber: repairNumber.toString() } },
        })
      ).json();
    },
    enabled: !!repairNumber,
  });
};

export const getAWAIssuesQueryOptions = ({
  assetId,
  status,
  orderBy,
}: {
  assetId: string;
  status?: IssueStatus[];
  orderBy?: Parameters<typeof AWAApiV2Service.GetIssues>[0]['orderBy'];
}) => {
  return queryOptions({
    queryKey: issueKeys.awaByAsset(assetId, status, orderBy),
    queryFn: async () =>
      (
        await (
          await AWAApiV2Service.GetIssues({
            limit: 100,
            filters: { state: RecordState.Active, fields: { assetId: [assetId], status } },
            orderBy,
          })
        ).json()
      ).data,
  });
};

export const getIssueByIdQueryOptions = ({ issueId, isAWA = false }: { issueId: string | undefined; isAWA?: boolean }) => {
  return queryOptions({
    queryKey: issueKeys.detail(issueId, isAWA),
    queryFn: async () => {
      if (!issueId) throw new Error('No issue ID');
      if (isAWA) {
        return (await (await AWAApiV2Service.GetIssueById(issueId)).json()).issue;
      } else {
        return (await (await ApiService.GetIssueById(issueId)).json()).issue;
      }
    },
    enabled: !!issueId,
  });
};

export const getIssueActionsInfiniteQueryOptions = ({ issueId, isAWA = false }: { issueId: string | undefined; isAWA?: boolean }) => {
  return infiniteQueryOptions({
    queryKey: issueKeys.actions(issueId, isAWA),
    queryFn: async ({ pageParam }: { pageParam?: string }) =>
      await (
        await (isAWA ? AWAApiV2Service : ApiService).GetActions({
          filters: { fields: { issueId: [issueId ?? ''] } },
          orderBy: { field: 'createdAt', direction: 'desc' },
          limit: 30,
          cursors: pageParam,
        })
      ).json(),
    getNextPageParam: (lastPage) => (lastPage.pageInfo.hasNextPage ? lastPage.pageInfo.endCursor : undefined),
    initialPageParam: undefined as string | undefined,
    enabled: !!issueId,
  });
};

export const getIssueCategoriesQueryOptions = ({ orgId }: { orgId: string | undefined }) => {
  return queryOptions({
    queryKey: issueCategoryKeys.list(orgId),
    queryFn: async () =>
      (
        await (
          await ApiService.GetCategories({
            filters: {
              fields: { entity: CategoryEntity.Issue },
            },
          })
        ).json()
      ).data,
  });
};

export const getIssuesInfiniteQueryOptions = ({
  filters,
  groupBy,
  orderBy,
  enabled = true,
}: {
  filters?: GetIssuesRequestDTO['filters'];
  groupBy?: GetIssuesRequestDTO['groupBy'];
  orderBy?: GetIssuesRequestDTO['orderBy'];
  enabled?: boolean;
}) => {
  return infiniteQueryOptions({
    queryKey: issueKeys.infinite(filters, groupBy, orderBy),
    queryFn: async ({ pageParam }: { pageParam?: string }) =>
      await (await ApiService.GetIssues({ limit: 30, cursors: pageParam, filters, groupBy, orderBy })).json(),
    getNextPageParam: (lastPage) => (lastPage.pageInfo.hasNextPage ? lastPage.pageInfo.endCursor : undefined),
    initialPageParam: undefined as string | undefined,
    retry: false,
    gcTime: 0,
    enabled,
  });
};

export const getIssueTagsQueryOptions = ({ orgId }: { orgId: string | undefined }) => {
  return queryOptions({
    queryKey: issueTagKeys.list(orgId),
    queryFn: async () =>
      (
        await (
          await ApiService.GetTags({
            filters: {
              fields: { entity: TagEntity.Issue },
            },
          })
        ).json()
      ).data,
  });
};
